import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/api';
import TopNav from '../components/TopNav';
import AlertModal from '../components/AlertModal';
import { useAuth } from '../context/AuthContext';
import { hasPageAuthority } from '../accessControl';
import { formatDateTimeConsistent } from '../utils/dateDisplay';
import './css/InventoryPage.css';
import './css/TransfersPage.css';
import './css/RepairOrdersPage.css';
import './css/ServiceRoomPage.css';

const SERVICE_STATUSES = ['Pending', 'In Progress', 'Completed'];
const LOCAL_TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/;

const formatDateTime = (value) => {
  return formatDateTimeConsistent(value, {
    timeZone: 'America/New_York',
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatLocalTimestampText = (value) => {
  if (!value) return '';
  const match = String(value).trim().match(LOCAL_TIMESTAMP_PATTERN);
  if (!match) return '';
  const [, year, month, day, hourText, minute, secondText = '00'] = match;
  const hour = Number(hourText);
  const second = Number(secondText);
  if (!Number.isFinite(hour) || !Number.isFinite(second)) return '';
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  const secondDisplay = second ? `:${String(second).padStart(2, '0')}` : '';
  return `${Number(month)}/${Number(day)}/${year}, ${hour12}:${minute}${secondDisplay} ${suffix}`;
};

const transactionTimestampDisplay = (value) => {
  const formatted = formatLocalTimestampText(value) || formatDateTime(value);
  if (!formatted) return { primary: '-', secondary: '' };
  const [primary, ...secondaryParts] = formatted.split(', ');
  return {
    primary,
    secondary: secondaryParts.join(', '),
  };
};

const renderTransactionTimestamp = (value) => {
  const display = transactionTimestampDisplay(value);
  return (
    <span className="service-room-date-stack">
      <span>{display.primary}</span>
      {display.secondary && <small className="transfer-date-note">{display.secondary}</small>}
    </span>
  );
};

const statusClassName = (status) => String(status || 'Pending')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

const subServiceNames = (service) => {
  const expandDefinition = (value) => {
    const text = String(value || '').trim();
    if (/^\d+$/.test(text)) {
      return Array.from({ length: Math.max(0, Number(text)) }, (_, index) => `Step ${index + 1}`);
    }
    return String(value || '').split(',');
  };
  const source = Array.isArray(service?.subServiceNames)
    ? service.subServiceNames
    : (Array.isArray(service?.sub_service_names)
      ? service.sub_service_names
      : expandDefinition(service?.subServices ?? service?.sub_services ?? ''));
  return [...new Set(source.map((name) => String(name || '').trim()).filter(Boolean))];
};

const getServiceStepRows = (service) => {
  if (Array.isArray(service.tasks) && service.tasks.length) {
    return service.tasks
      .map((task) => ({
        id: task.id || `${service.id}-${task.subService || 'service'}-${task.groupId || task.groupName || ''}`,
        step: Number.isFinite(Number(task.subService)) ? Number(task.subService) : null,
        label: task.subService ? String(task.subService) : '',
        subService: task.subService ?? null,
        groupId: task.groupId ?? null,
        groupName: task.groupName || '',
        status: task.status || 'Pending',
        worker: task.worker || '',
        inProgressAt: task.inProgressAt || null,
        completedAt: task.completedAt || null,
        current: task.status === 'In Progress',
      }))
      .filter((task) => (Number.isFinite(task.step) && task.step > 0) || task.groupName)
      .sort((a, b) => (a.step || 0) - (b.step || 0));
  }

  const currentStep = Number(service.currentStep);
  if (Number.isFinite(currentStep) && currentStep > 0) {
    return [{
      id: `${service.id}-${currentStep}-${service.currentGroupId || service.currentGroupName || ''}`,
      step: currentStep,
      label: `Step ${currentStep}`,
      subService: currentStep,
      groupId: service.currentGroupId ?? null,
      groupName: service.currentGroupName || '',
      status: service.serviceStatus || service.status || 'Pending',
      worker: service.worker || service.serviceWorker || '',
      inProgressAt: service.serviceInProgressAt || null,
      completedAt: service.serviceCompletedAt || null,
      current: true,
    }];
  }

  const groupAssignments = Array.isArray(service.groupAssignments) ? service.groupAssignments : [];
  const assignedSteps = groupAssignments
    .map((assignment) => ({
      step: Number(assignment.subService),
      label: assignment.subService ? String(assignment.subService) : '',
      id: `${service.id}-${assignment.subService || 'service'}-${assignment.groupId || assignment.groupName || ''}`,
      subService: assignment.subService ?? null,
      groupId: assignment.groupId ?? null,
      groupName: assignment.groupName || '',
      status: service.serviceStatus || service.status || 'Pending',
      worker: service.worker || service.serviceWorker || '',
      inProgressAt: service.serviceInProgressAt || null,
      completedAt: service.serviceCompletedAt || null,
      current: false,
    }))
      .filter((assignment) => (Number.isFinite(assignment.step) && assignment.step > 0) || assignment.label || assignment.groupName);
  if (assignedSteps.length) {
    return assignedSteps.sort((a, b) => (a.step || 0) - (b.step || 0));
  }

  return subServiceNames(service).map((name, index) => ({
    step: Number.isFinite(Number(name)) ? Number(name) : null,
    label: name,
    id: `${service.id}-${name || index + 1}`,
    subService: name,
    groupId: null,
    groupName: '',
    status: service.serviceStatus || service.status || 'Pending',
    worker: service.worker || service.serviceWorker || '',
    inProgressAt: service.serviceInProgressAt || null,
    completedAt: service.serviceCompletedAt || null,
    current: false,
  }));
};

const ServiceRoomPage = () => {
  const { user } = useAuth();
  const [serial, setSerial] = useState('');
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingServiceId, setSavingServiceId] = useState(null);
  const [warning, setWarning] = useState('');
  const serialInputRef = useRef(null);

  const services = useMemo(() => (order?.services || []).map((service) => ({
    ...service,
    id: service.serviceRoomId || service.id,
  })), [order]);
  const completedCount = useMemo(() => (
    services.reduce((count, service) => {
      const taskRows = getServiceStepRows(service);
      if (taskRows.length) {
        return count + taskRows.filter((task) => task.status === 'Completed').length;
      }
      return count + ((service.serviceStatus || service.status) === 'Completed' ? 1 : 0);
    }, 0)
  ), [services]);
  const taskCount = useMemo(() => (
    services.reduce((count, service) => {
      const taskRows = getServiceStepRows(service);
      return count + (taskRows.length || 1);
    }, 0)
  ), [services]);
  const workerName = user?.name || user?.username || '';
  const isTechnician = String(user?.role || '').toLowerCase() === 'technician';
  const canEditServiceRoom = hasPageAuthority(user, 'service-room', 'full');

  useEffect(() => {
    document.title = "Service Room - Services Irene's Wigs.com";
  }, []);

  useEffect(() => {
    if (!isTechnician || loading || !status) return undefined;
    const timer = setTimeout(() => {
      setSerial('');
      setOrder(null);
      setStatus('');
      setWarning('');
      serialInputRef.current?.focus();
    }, 10000);
    return () => clearTimeout(timer);
  }, [isTechnician, loading, status]);

  const findOpenOrder = async (event) => {
    event.preventDefault();
    if (loading) return;
    const serialText = serial.trim();
    if (!serialText) {
      setStatus('Enter a serial number to scan.');
      return;
    }

    setLoading(true);
    setWarning('');
    setStatus('');
    setOrder(null);
    try {
      if (isTechnician) {
        const response = await api.post('/repair-orders/service-room/scan', {
          serial: serialText,
          assignedServices: user?.technicianServices || [],
          technicianServiceGroupId: user?.technicianServiceGroupId || null,
        });
        setOrder(response.data.order);
        setStatus(response.data.message || `Loaded repair order #${response.data.order?.id}.`);
        return;
      }

      const response = await api.get('/repair-orders/service-room/open-by-serial', {
        params: { serial: serialText },
      });
      if (!response.data) {
        setStatus(`No open repair order found for serial ${serialText}.`);
        return;
      }
      setOrder(response.data);
      setStatus(`Loaded repair order #${response.data.id}.`);
    } catch (error) {
      if (error.response?.data?.code === 'MULTIPLE_OPEN_REPAIR_ORDERS') {
        setWarning(error.response.data.error);
      }
      setStatus(error.response?.data?.error || error.message || 'Failed to load repair order.');
    } finally {
      setLoading(false);
    }
  };

  const updateServiceStatus = async (service, nextStatus) => {
    if (!service?.id || !nextStatus) return;
    setSavingServiceId(service.id);
    setStatus('');
    try {
      const response = await api.patch(`/repair-orders/service-room/services/${encodeURIComponent(service.id)}/status`, {
        status: nextStatus,
        updatedBy: workerName,
      });
      setOrder(response.data);
      setStatus(`${service.name || 'Service'} marked ${nextStatus}.`);
    } catch (error) {
      setStatus(error.response?.data?.error || error.message || 'Failed to update service status.');
    } finally {
      setSavingServiceId(null);
    }
  };

  const updateServiceTaskStatus = async (service, task, nextStatus) => {
    if (!service?.id || !task || !nextStatus) return;
    setSavingServiceId(`${service.id}-${task.id}`);
    setStatus('');
    try {
      const response = await api.patch(`/repair-orders/service-room/services/${encodeURIComponent(service.id)}/tasks/status`, {
        subService: task.subService,
        groupId: task.groupId,
        groupName: task.groupName,
        status: nextStatus,
      });
      setOrder(response.data);
      setStatus(`${task.label || (task.step ? `Step ${task.step}` : (task.groupName || 'Task'))} marked ${nextStatus}.`);
    } catch (error) {
      setStatus(error.response?.data?.error || error.message || 'Failed to update task status.');
    } finally {
      setSavingServiceId(null);
    }
  };

  return (
    <>
      <TopNav />
      {warning && <AlertModal message={warning} onClose={() => setWarning('')} />}
      <main className="inventory-container transfers-page repair-page service-room-page">
        <h1>Service Room</h1>
        {status && <div className="transfer-status repair-status" role="status">{status}</div>}

        <form className="inventory-form transfer-toolbar service-room-scan-row" onSubmit={findOpenOrder}>
          <input
            ref={serialInputRef}
            value={serial}
            onChange={(event) => setSerial(event.target.value)}
            placeholder="Scan serial #"
            autoFocus
          />
          <button type="submit" className="button blue" disabled={loading}>
            {loading ? 'Loading...' : 'Find'}
          </button>
        </form>

        {!isTechnician && <section className="inventory-card transfer-results service-room-order-card">
          {order ? (
            <>
              <div className="transfer-results-header service-room-header">
                <div>
                  <h3>Repair Order #{order.id}</h3>
                  <p>{order.customerName || 'No customer'} - {order.serial || 'No serial'}</p>
                </div>
                <span className={`repair-service-status-badge ${completedCount === taskCount && taskCount ? 'completed' : 'in-progress'}`}>
                  {completedCount}/{taskCount} completed
                </span>
              </div>

              <div className="repair-serial-grid service-room-summary">
                <div>
                  <span>Item</span>
                  <strong>{order.itemDescription || order.itemName || '-'}</strong>
                </div>
                <div>
                  <span>Order Date</span>
                  <strong>{renderTransactionTimestamp(order.orderDate)}</strong>
                </div>
                <div>
                  <span>Warranty</span>
                  <strong>{order.warrantyStatus || '-'}</strong>
                </div>
                <div>
                  <span>Location</span>
                  <strong>{order.currentLocation || '-'}</strong>
                </div>
              </div>

              <div className="repair-services-table-wrap">
                <table className="inventory-table repair-services-table service-room-table">
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Description</th>
                      <th>Group</th>
                      <th>Status</th>
                      <th>Worker</th>
                      <th>Started</th>
                      <th>Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((service) => {
                      const currentStatus = service.serviceStatus || service.status || 'Pending';
                      const stepRows = getServiceStepRows(service);
                      const saving = savingServiceId === service.id;
                      return (
                        <tr key={service.id}>
                          <td>{service.name || '-'}</td>
                          <td>{service.description || '-'}</td>
                          <td>
                            {stepRows.length ? (
                              <div className="repair-service-group-stack service-room-group-stack">
                                {stepRows.map((stepRow) => (
                                  <label
                                    className={`repair-service-group-assignment${stepRow.label ? '' : ' single'}`}
                                    key={`${service.id}-step-${stepRow.step || stepRow.groupName || 'service'}`}
                                  >
                                    {stepRow.label && <span>{stepRow.label}</span>}
                                    <select value={stepRow.groupName || ''} disabled>
                                      <option value={stepRow.groupName || ''}>{stepRow.groupName || 'No group'}</option>
                                    </select>
                                  </label>
                                ))}
                              </div>
                            ) : '-'}
                          </td>
                          <td>
                            {stepRows.length ? (
                              <div className="service-room-task-stack">
                                {stepRows.map((stepRow) => (
                                  <select
                                    value={stepRow.status}
                                    onChange={(event) => updateServiceTaskStatus(service, stepRow, event.target.value)}
                                    disabled={!canEditServiceRoom || savingServiceId === `${service.id}-${stepRow.id}`}
                                    title={!canEditServiceRoom ? 'This access group has read-only Service Room access.' : undefined}
                                    className={`service-room-status-select ${statusClassName(stepRow.status)}`}
                                    key={`${service.id}-status-${stepRow.step || stepRow.groupName || 'service'}`}
                                  >
                                    {SERVICE_STATUSES.map((option) => (
                                      <option value={option} key={option}>{option}</option>
                                    ))}
                                  </select>
                                ))}
                              </div>
                            ) : (
                              <select
                                value={currentStatus}
                                onChange={(event) => updateServiceStatus(service, event.target.value)}
                                disabled={saving || !canEditServiceRoom}
                                title={!canEditServiceRoom ? 'This access group has read-only Service Room access.' : undefined}
                                className={`service-room-status-select ${statusClassName(currentStatus)}`}
                              >
                                {SERVICE_STATUSES.map((option) => (
                                  <option value={option} key={option}>{option}</option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            {stepRows.length ? (
                              <div className="service-room-task-stack">
                                {stepRows.map((stepRow) => <span key={`${service.id}-worker-${stepRow.step || stepRow.groupName || 'service'}`}>{stepRow.worker || '-'}</span>)}
                              </div>
                            ) : (service.worker || service.serviceWorker || '-')}
                          </td>
                          <td>
                            {stepRows.length ? (
                              <div className="service-room-task-stack">
                                {stepRows.map((stepRow) => <span key={`${service.id}-started-${stepRow.step || stepRow.groupName || 'service'}`}>{renderTransactionTimestamp(stepRow.inProgressAt)}</span>)}
                              </div>
                            ) : renderTransactionTimestamp(service.serviceInProgressAt)}
                          </td>
                          <td>
                            {stepRows.length ? (
                              <div className="service-room-task-stack">
                                {stepRows.map((stepRow) => <span key={`${service.id}-completed-${stepRow.step || stepRow.groupName || 'service'}`}>{renderTransactionTimestamp(stepRow.completedAt)}</span>)}
                              </div>
                            ) : renderTransactionTimestamp(service.serviceCompletedAt)}
                          </td>
                        </tr>
                      );
                    })}
                    {services.length === 0 && (
                      <tr>
                        <td colSpan="7">No repair services found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="repair-empty-state">Scan an item to load its open repair order.</div>
          )}
        </section>}
      </main>
    </>
  );
};

export default ServiceRoomPage;
